import {
  BooleanOperator,
  ConditionTab,
  ConditionType,
  DatetimeOperator,
  FrequencyOperator,
  ListOperator,
  NumberOperator,
  StringOperator,
  Type,
} from './enums/builder-enums';
import {IntegrationType} from './enums/connect-enums';
import {Property, WindowUnit} from './enums/expression-enums';
import {FilterOperator} from './types/builder-state-types';

export const CONDITION_TYPE_TO_TAB_MAPPING = {
  [ConditionType.Cohorts]: ConditionTab.Cohorts,
  [ConditionType.Events]: ConditionTab.Events,
  [ConditionType.People]: ConditionTab.People,
};

export const LITERAL_NAMES = [
  `A`,
  `B`,
  `C`,
  `D`,
  `E`,
  `F`,
  `G`,
  `H`,
  `I`,
  `J`,
  `K`,
  `L`,
  `M`,
  `N`,
  `O`,
  `P`,
  `Q`,
  `R`,
  `S`,
  `T`,
  `U`,
  `V`,
  `W`,
  `X`,
  `Y`,
  `Z`,
];
export const MAX_LITERALS = LITERAL_NAMES.length;
export const MAX_CONDITIONS = 15;
export const LITERAL_NAME_REGEX = new RegExp(`[${LITERAL_NAMES.join(``)}]\\d*`, `g`);

export const DEFAULT_FREQUENCY_VALUE = 1;
export const ANY_EVENT = ``;
export const FIRST_EVENT = `$first_event`;
export const DEFAULT_EVENT = ANY_EVENT;

export const DEFAULT_RELATIVE_NUM_DAYS = 7;
export const DEFAULT_WINDOW_UNIT = WindowUnit.Day;

export const DEFAULT_PEOPLE_PROPERTY_OPTIONS = {
  operator: DatetimeOperator.Relative,
  property: `$created`,
  propertyType: Property.User,
  type: Type.Datetime,
  value: {
    window: {
      unit: WindowUnit.Day,
      value: 30,
    },
  },
};
export const MAX_LABEL_ENTRIES = 3;
export const SHOW_MORE_LIMIT = 5;
export const TABS_LIMIT_NORMAL_WIDTH = 4;
export const MAX_COHORT_NAME_LENGTH = 190;
export const FUNNEL_CLAUSE_OPERATOR: any = null;

export const FREQUENCY_OPERATOR_LIST = [
  FrequencyOperator.Between,
  FrequencyOperator.AtLeast,
  FrequencyOperator.GreaterThan,
  FrequencyOperator.LessThan,
  FrequencyOperator.EqualTo,
];

export const FILTER_TYPE_LIST = [Type.String, Type.Number, Type.Boolean, Type.Datetime, Type.List];

const STRING_FILTER_OPERATOR_LIST = [
  StringOperator.Contains,
  StringOperator.DoesNotContain,
  StringOperator.Equals,
  StringOperator.DoesNotEqual,
  StringOperator.Set,
  StringOperator.NotSet,
];

const NUMBER_FILTER_OPERATOR_LIST = [
  NumberOperator.Between,
  NumberOperator.AtLeast,
  NumberOperator.GreaterThan,
  NumberOperator.LessThan,
  NumberOperator.EqualTo,
  NumberOperator.NotEqualTo,
];

const DATETIME_FILTER_OPERATOR_LIST = [
  DatetimeOperator.Between,
  DatetimeOperator.After,
  DatetimeOperator.On,
  DatetimeOperator.RelativeBeforeInPast,
  DatetimeOperator.Relative,
  DatetimeOperator.RelativeFuture,
];

export const FILTER_OPERATORS: {[filter: string]: FilterOperator[]} = {
  [Type.String]: STRING_FILTER_OPERATOR_LIST,
  [Type.Number]: NUMBER_FILTER_OPERATOR_LIST,
  [Type.Datetime]: DATETIME_FILTER_OPERATOR_LIST,
  [Type.Boolean]: [BooleanOperator.True, BooleanOperator.False],
  [Type.List]: [ListOperator.Contains, ListOperator.DoesNotContain],
};

export const DEFAULT_FILTER_OPERATOR: {[filter: string]: FilterOperator} = {
  [Type.String]: StringOperator.Equals,
  [Type.Number]: NumberOperator.Between,
  [Type.Datetime]: DatetimeOperator.Relative,
  [Type.Boolean]: BooleanOperator.True,
  [Type.List]: ListOperator.Contains,
};

export const EXPORT_INTEGRATIONS_INFO = {
  [IntegrationType.Braze]: {
    exportStartedMessage: `Your cohort export to Braze has begun.`,
    helpLink: `https://help.mixpanel.com/hc/en-us/articles/360001243663#use-the-mixpanel-cohort-to-create-a-segment-in-braze`,
    helpLinkText: `Setting up the Mixpanel Cohort in Braze`,
    noUsersText: `There are no user profiles with a Braze external user id in this cohort`,
    tooltipText: `Only user profiles with a Braze external user id will be exported`,
  },
  [IntegrationType.Marketo]: {
    duplicateExternalInfoError: `This custom field has already been used`,
    exportStartedMessage: `Your cohort export to Marketo has begun.`,
    externalInfoInputData: {
      fieldColumnHeader: `Custom field name`,
      param: `marketo_custom_field_name`,
      placeholder: `Marketo Custom Field to update`,
      title: `Custom field name`,
    },
    helpLink:
      `https://help.mixpanel.com/hc/en-us/articles/115005719646-Integrating-Mixpanel-and-Marketo` +
      `#marketo-integration-setup`,
    helpLinkText: `Setting up Custom Fields in Marketo`,
    maxExportUsers: 30000000,
    maxExportUsersText: `The maximum exportable cohort size is 30 million user profiles at this time.`,
    noUsersText: `There are no user profiles with $email in this cohort`,
    tooltipText: `Only user profiles with $email will be exported`,
  },
  [IntegrationType.UrbanAirship]: {
    duplicateExternalInfoError: `This tag has already been used`,
    exportStartedMessage: `Your cohort export to Urban Airship has begun.`,
    externalInfoInputData: {
      fieldColumnHeader: `Tag name`,
      param: `tag`,
      placeholder: `Urban Airship tag to add`,
      title: `Urban Airship tag`,
    },
    helpLink:
      `https://help.mixpanel.com/hc/en-us/articles/115005712363-Integrating-Mixpanel-and-Urban-Airship` +
      `#setting-up-the-urban-airship-integration`,
    helpLinkText: `Setting up the Mixpanel Tag Group in UA`,
    maxExportUsers: 15000000,
    maxExportUsersText: `The maximum exportable cohort size is 15 million user profiles at this time.`,
    noUsersText: `There are no user profiles with an Urban Airship channel id in this cohort`,
    tooltipText: `Only user profiles with an Urban Airship channel id will be exported`,
  },
  [IntegrationType.FacebookAds]: {
    helpLink:
      `https://help.mixpanel.com/hc/en-us/articles/115005712363-Integrating-Mixpanel-and-Urban-Airship` +
      `#setting-up-the-urban-airship-integration`,
    helpLinkText: `Setting up the Facebook Ads Integration`,
    exportStartedMessage: `Your cohort export to Facebook Ads has begun.`,
    tooltipText: `Only user profiles with an email, a phone number, a first name, and a last name will be exported`,
  },
  [IntegrationType.Iterable]: {
    helpLink:
      `https://help.mixpanel.com/hc/en-us/articles/115005712363-Integrating-Mixpanel-and-Urban-Airship` +
      `#setting-up-the-urban-airship-integration`,
    helpLinkText: `Setting up the Iterable Integration`,
    exportStartedMessage: `Your cohort export to Iterable has begun.`,
    tooltipText: `Only user profiles with an email will be exported`,
  },
  [IntegrationType.GoogleAds]: {
    helpLink:
      `https://help.mixpanel.com/hc/en-us/articles/115005712363-Integrating-Mixpanel-and-Urban-Airship` +
      `#setting-up-the-urban-airship-integration`,
    helpLinkText: `Setting up the Google Adwords Integration`,
    exportStartedMessage: `Your cohort export to Google Adwords has begun.`,
    tooltipText: `Only user profiles with an email and a phone number will be exported`,
  },
};

export const EXPORT_TOGGLE_OPTIONS = [
  {
    text: {
      [IntegrationType.Braze]: `All users in the cohort will be updated one time.`,
      [IntegrationType.Marketo]: `All users in the cohort will be updated with the custom field one time.`,
      [IntegrationType.UrbanAirship]: `All users in the cohort will be updated with the tag one time.`,
      [IntegrationType.FacebookAds]: `All users in the cohort will be uploaded to the Business Manager, replacing the old one.`,
      [IntegrationType.GoogleAds]: `All users in the cohort will be uploaded as a Custom Audience, replacing the old one.`,
      [IntegrationType.Iterable]: `All users in the cohort will as an Iterable List, replacing the old one.`,
    },
    title: `One-time export`,
    type: `static`,
  },
  {
    text: {
      [IntegrationType.Braze]: `Your Braze cohort will be updated every 2 hours to match users in Mixpanel cohort.`,
      [IntegrationType.Marketo]: `The custom field will be updated every 2 hours to match users in the cohort.`,
      [IntegrationType.UrbanAirship]: `Tag will be updated every 2 hours to match users in the cohort.`,
      [IntegrationType.FacebookAds]: `Your Facebook Custom Audience will be updated every 2 hours to match users in this cohort.`,
      [IntegrationType.GoogleAds]: `Your Google Custom Audience will be updated every 2 hours to match users in this cohort.`,
      [IntegrationType.Iterable]: `Your Iterable List will be updated every 2 hours to match users in this cohort.`,
    },
    title: `Dynamic sync`,
    type: `dynamic`,
  },
];
